import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  Event,
  EventType,
  EventStatus,
  STATUS_CONFIG,
} from '../types';
import {
  EVENT_FIELD_CONFIG,
  PRIMARY_DATE_KEY,
  END_DATE_KEY,
  type LogicalEventType,
  type FieldDef,
  type FieldSection,
} from '../eventFieldConfig';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #e5e7eb;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FieldLabel = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  color: #8b8b93;
`;

/** Two-column grid; fields without `half` span both columns. */
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 10px;
`;

const GridCell = styled.div<{ $full: boolean }>`
  grid-column: ${({ $full }) => ($full ? '1 / -1' : 'auto')};
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin-top: 4px;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #2a2a2e;
  }
`;

const DateTimeRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 10px;
`;

const BaseControl = `
  width: 100%;
  height: 36px;
  padding: 0 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #2a2a2e;
  color: #e5e7eb;
  font-size: 0.875rem;
  outline: none;
  box-sizing: border-box;
  background: #191919;
  transition: border-color 120ms;

  &:hover {
    border-color: #3f3f46;
  }
  &:focus {
    border-color: #71717a;
  }
`;

const Input = styled.input`
  ${BaseControl}
`;

const Select = styled.select`
  ${BaseControl}
  appearance: none;
  -webkit-appearance: none;
  padding-right: 2.25rem;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px;
  cursor: pointer;
`;

const CheckboxWrap = styled.label`
  display: flex;
  align-items: center;
  height: 36px;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #e5e7eb;
  cursor: pointer;
  input {
    width: 1rem;
    height: 1rem;
    accent-color: #e5e7eb;
  }
`;

const TextArea = styled.textarea`
  ${BaseControl}
  height: auto;
  padding: 0.5rem 0.75rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
`;

const Divider = styled.div`
  height: 1px;
  background: #2a2a2e;
  margin: 2px 0;
  flex-shrink: 0;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 1.25rem;

  /* Scrollbar: show only on hover (WebKit) */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover {
    scrollbar-color: #383836 transparent;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
  }
  &:hover::-webkit-scrollbar-thumb {
    background: #383836;
  }
`;

export type Category = '' | 'travel' | 'stay' | 'reservation' | 'activity';

interface EventFormProps {
  onCreate: (event: Omit<Event, 'id'>) => void;
  /** Id for the form element (for external submit button via form attribute). */
  formId?: string;
  /** When set, Step 1 (category) is hidden and form starts with this category. */
  initialCategory?: Category;
  /** When set with initialCategory, subtype is pre-selected. */
  initialSubType?: SubType;
  /** When adding from calendar day + icon, pre-fill the primary date with this. */
  initialDate?: Date;
  /** Reports whether the required fields are filled, so an external submit button can enable. */
  onValidityChange?: (valid: boolean) => void;
}

type SubType =
  | ''
  // Travel
  | 'flight'
  | 'train'
  | 'bus'
  | 'ferry'
  | 'car-rental'
  | 'road-trip'
  // Stay
  | 'hotel'
  | 'airbnb'
  | 'hostel'
  | 'resort'
  | 'camping'
  // Reservation
  | 'restaurant'
  | 'spa'
  | 'tour'
  | 'rental-non-car'
  | 'appointment'
  // Activity
  | 'concert'
  | 'museum'
  | 'hike'
  | 'ski-day'
  | 'game'
  | 'attraction';

const SUBTYPE_OPTIONS: Record<Category, { value: SubType; label: string; eventType: EventType }[]> = {
  travel: [
    { value: 'flight', label: 'Flight', eventType: 'flight' },
    { value: 'train', label: 'Train', eventType: 'transport' },
    { value: 'bus', label: 'Bus', eventType: 'transport' },
    { value: 'ferry', label: 'Ferry', eventType: 'transport' },
    { value: 'car-rental', label: 'Car Rental', eventType: 'transport' },
    { value: 'road-trip', label: 'Road Trip Segment', eventType: 'transport' },
  ],
  stay: [
    { value: 'hotel', label: 'Hotel', eventType: 'hotel' },
    { value: 'airbnb', label: 'Airbnb', eventType: 'hotel' },
    { value: 'hostel', label: 'Hostel', eventType: 'hotel' },
    { value: 'resort', label: 'Resort', eventType: 'hotel' },
    { value: 'camping', label: 'Camping', eventType: 'hotel' },
  ],
  reservation: [
    { value: 'restaurant', label: 'Restaurant', eventType: 'activity' },
    { value: 'spa', label: 'Spa', eventType: 'activity' },
    { value: 'tour', label: 'Tour', eventType: 'activity' },
    { value: 'rental-non-car', label: 'Rental (non-car)', eventType: 'activity' },
    { value: 'appointment', label: 'Appointment', eventType: 'activity' },
  ],
  activity: [
    { value: 'concert', label: 'Concert', eventType: 'activity' },
    { value: 'museum', label: 'Museum', eventType: 'activity' },
    { value: 'hike', label: 'Hike', eventType: 'activity' },
    { value: 'ski-day', label: 'Ski Day', eventType: 'activity' },
    { value: 'game', label: 'Game', eventType: 'activity' },
    { value: 'attraction', label: 'Attraction', eventType: 'activity' },
  ],
  '': [],
};

function subTypeToEventType(category: Category, subtype: SubType): EventType {
  const options = SUBTYPE_OPTIONS[category] || [];
  const found = options.find((x) => x.value === subtype);
  return found ? found.eventType : 'other';
}

function getLogicalType(category: Category, subType: SubType): LogicalEventType | null {
  if (!category || !subType) return null;
  if (category === 'travel') {
    if (subType === 'flight') return 'Flight';
    if (subType === 'train') return 'Train';
    if (subType === 'bus') return 'Bus';
    if (subType === 'ferry') return 'Ferry';
    if (subType === 'car-rental') return 'Car Rental';
    if (subType === 'road-trip') return 'Road Trip';
    return null;
  }
  if (category === 'stay') return 'Stay';
  if (category === 'reservation') return subType === 'restaurant' ? 'Restaurant' : 'Reservation';
  if (category === 'activity') return 'Activity / Event';
  return null;
}

/** Parse YYYY-MM-DD and optional HH:mm or HH:mm:ss as local time (avoids UTC shift). */
function parseLocalDateTime(dateStr: string, timeStr?: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  if (timeStr) {
    const parts = timeStr.split(':');
    const h = Number(parts[0]) || 0;
    const min = Number(parts[1]) || 0;
    const sec = Number(parts[2]) || 0;
    return new Date(y, m - 1, d, h, min, sec, 0);
  }
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

type FormValues = Record<string, string | number | boolean>;

export const EVENT_CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: 'travel', label: 'Travel' },
  { value: 'stay', label: 'Stay' },
  { value: 'reservation', label: 'Reservation' },
  { value: 'activity', label: 'Activity' },
];

const EventForm: React.FC<EventFormProps> = ({ onCreate, formId = 'event-drawer-form', initialCategory = '', initialSubType = '', initialDate, onValidityChange }) => {
  const [category, setCategory] = useState<Category>(initialCategory);
  const [subType, setSubType] = useState<SubType>(initialSubType);
  const [name, setName] = useState('');
  const [status, setStatus] = useState<EventStatus>('planning');
  const [formValues, setFormValues] = useState<FormValues>({});

  React.useEffect(() => {
    setCategory(initialCategory);
    setSubType(initialSubType);
    setFormValues({});
  }, [initialCategory, initialSubType]);

  React.useEffect(() => {
    if (!initialDate || !category) return;
    const logical = getLogicalType(category, subType);
    if (!logical) return;
    const primaryKey = PRIMARY_DATE_KEY[logical];
    const fieldList = EVENT_FIELD_CONFIG[logical];
    const field = fieldList?.find((f: FieldDef) => f.key === primaryKey);
    if (!field || !primaryKey) return;
    const dateStr = initialDate.toISOString().slice(0, 10);
    if (field.inputType === 'date') {
      setFormValues(prev => ({ ...prev, [primaryKey]: dateStr }));
    } else if (field.inputType === 'datetime') {
      const timeStr = initialDate.toTimeString().slice(0, 5);
      setFormValues(prev => ({ ...prev, [primaryKey]: dateStr, [primaryKey + 'Time']: timeStr }));
    }
  }, [initialDate, category, subType]);

  const logicalType = getLogicalType(category, subType);
  const fields = logicalType ? EVENT_FIELD_CONFIG[logicalType] : [];
  const isPreselected = !!initialCategory;

  const primaryDateKey = logicalType ? PRIMARY_DATE_KEY[logicalType] : undefined;
  const isValid =
    !!category &&
    !!logicalType &&
    (!SUBTYPE_OPTIONS[category]?.length || !!subType) &&
    name.trim().length > 0 &&
    (!primaryDateKey || !!formValues[primaryDateKey]);

  React.useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleCategoryChange = (c: Category) => {
    setCategory(c);
    setSubType('');
    setFormValues({});
  };

  const setFormValue = useCallback((key: string, value: string | number | boolean) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const type: EventType = subTypeToEventType(category, subType);
    if (!category || !logicalType) return;
    if (SUBTYPE_OPTIONS[category]?.length && !subType) return;
    if (!name.trim()) return;

    const primaryKey = PRIMARY_DATE_KEY[logicalType];
    let eventDate: Date;
    if (primaryKey) {
      const dateVal = formValues[primaryKey];
      const timeVal = formValues[primaryKey + 'Time'];
      if (typeof dateVal === 'string' && dateVal) {
        const timePart =
          typeof timeVal === 'string' && timeVal ? timeVal : '12:00';
        eventDate = parseLocalDateTime(dateVal, timePart);
      } else {
        eventDate = new Date();
      }
    } else {
      eventDate = new Date();
    }

    const details: Record<string, string | number | boolean> = {};
    for (const field of fields) {
      if (field.key === 'title' || field.key === 'status') continue;
      if (field.inputType === 'datetime') {
        const d = formValues[field.key];
        const t = formValues[field.key + 'Time'];
        if (d && typeof d === 'string')
          details[field.key] = t ? `${d}T${t}` : `${d}T12:00:00`;
        continue;
      }
      const v = formValues[field.key];
      if (v !== undefined && v !== '' && (typeof v !== 'number' || !Number.isNaN(v))) {
        details[field.key] = v;
      }
    }

    let endDate: Date | undefined;
    const endKey = logicalType ? END_DATE_KEY[logicalType] : undefined;
    if (endKey && details[endKey]) {
      const raw = details[endKey];
      if (typeof raw === 'string') {
        if (raw.includes('T')) {
          const [datePart, timePart] = raw.split('T');
          if (datePart) endDate = parseLocalDateTime(datePart, timePart || undefined);
        } else {
          endDate = parseLocalDateTime(raw, '23:59:59');
        }
        if (endDate && Number.isNaN(endDate.getTime())) endDate = undefined;
      }
    }

    onCreate({
      name: name.trim(),
      type,
      status,
      date: eventDate,
      endDate,
      location: details['address'] as string || details['location'] as string || undefined,
      notes: (details['notes'] as string) || undefined,
      details: Object.keys(details).length ? details : undefined,
    });

    setName('');
    setStatus('planning');
    setCategory('');
    setSubType('');
    setFormValues({});
  };

  const renderField = (field: FieldDef) => {
    const label = (
      <FieldLabel>
        {field.label}
        {field.required && ' *'}
      </FieldLabel>
    );
    if (field.key === 'title') {
      return (
        <FieldGroup key={field.key}>
          {label}
          <Input
            placeholder={field.placeholder ?? 'Add a title'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FieldGroup>
      );
    }
    if (field.key === 'status') {
      return (
        <FieldGroup key={field.key}>
          {label}
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as EventStatus)}
          >
            {(Object.entries(STATUS_CONFIG) as [EventStatus, { label: string }][]).map(
              ([value, config]) => (
                <option key={value} value={value}>
                  {config.label}
                </option>
              )
            )}
          </Select>
        </FieldGroup>
      );
    }
    if (field.inputType === 'datetime') {
      const dateVal = (formValues[field.key] ?? '') as string;
      const timeVal = (formValues[field.key + 'Time'] ?? '12:00') as string;
      return (
        <FieldGroup key={field.key}>
          {label}
          <DateTimeRow>
            <Input
              type="date"
              value={dateVal}
              onChange={(e) => setFormValue(field.key, e.target.value)}
            />
            <Input
              type="time"
              value={timeVal}
              onChange={(e) => setFormValue(field.key + 'Time', e.target.value)}
            />
          </DateTimeRow>
        </FieldGroup>
      );
    }
    if (field.inputType === 'date') {
      return (
        <FieldGroup key={field.key}>
          {label}
          <Input
            type="date"
            value={(formValues[field.key] ?? '') as string}
            onChange={(e) => setFormValue(field.key, e.target.value)}
          />
        </FieldGroup>
      );
    }
    if (field.inputType === 'time') {
      return (
        <FieldGroup key={field.key}>
          {label}
          <Input
            type="time"
            value={(formValues[field.key] ?? '') as string}
            onChange={(e) => setFormValue(field.key, e.target.value)}
          />
        </FieldGroup>
      );
    }
    if (field.inputType === 'checkbox') {
      return (
        <FieldGroup key={field.key}>
          <CheckboxWrap>
            <input
              type="checkbox"
              checked={!!formValues[field.key]}
              onChange={(e) => setFormValue(field.key, e.target.checked)}
            />
            <span>{field.label}</span>
          </CheckboxWrap>
        </FieldGroup>
      );
    }
    if (field.inputType === 'select') {
      const options = field.key === 'status' ? [] : field.options ?? [];
      return (
        <FieldGroup key={field.key}>
          {label}
          <Select
            value={(formValues[field.key] ?? '') as string}
            onChange={(e) => setFormValue(field.key, e.target.value)}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FieldGroup>
      );
    }
    if (field.inputType === 'number') {
      const v = formValues[field.key];
      const displayValue = v === undefined || v === '' ? '' : String(v);
      return (
        <FieldGroup key={field.key}>
          {label}
          <Input
            type="number"
            value={displayValue}
            onChange={(e) =>
              setFormValue(field.key, e.target.value === '' ? '' : Number(e.target.value))
            }
          />
        </FieldGroup>
      );
    }
    if (field.inputType === 'url') {
      return (
        <FieldGroup key={field.key}>
          {label}
          <Input
            type="url"
            placeholder="https://"
            value={(formValues[field.key] ?? '') as string}
            onChange={(e) => setFormValue(field.key, e.target.value)}
          />
        </FieldGroup>
      );
    }
    // text or default
    const isNotes = field.key === 'notes';
    return (
      <FieldGroup key={field.key}>
        {label}
        {isNotes ? (
          <TextArea
            placeholder="Optional"
            value={(formValues[field.key] ?? '') as string}
            onChange={(e) => setFormValue(field.key, e.target.value)}
          />
        ) : (
          <Input
            type="text"
            placeholder={field.placeholder}
            value={(formValues[field.key] ?? '') as string}
            onChange={(e) => setFormValue(field.key, e.target.value)}
          />
        )}
      </FieldGroup>
    );
  };

  const renderSection = (section: FieldSection | undefined, heading?: string) => {
    const sectionFields = fields.filter((f) => f.section === section);
    if (sectionFields.length === 0) return null;
    return (
      <>
        {heading && <SectionLabel>{heading}</SectionLabel>}
        <FieldGrid>
          {sectionFields.map((field) => (
            <GridCell key={field.key} $full={!field.half}>
              {renderField(field)}
            </GridCell>
          ))}
        </FieldGrid>
      </>
    );
  };

  return (
    <Form id={formId} onSubmit={handleSubmit}>
      <ScrollArea>
        {!isPreselected && (
          <FieldGroup>
            <FieldLabel>Type</FieldLabel>
            <Select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value as Category)}
            >
              <option value="">Select type</option>
              {EVENT_CATEGORY_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FieldGroup>
        )}

        {category && SUBTYPE_OPTIONS[category]?.length > 0 && (
          <FieldGroup>
            <FieldLabel>
              {category === 'travel'
                ? 'Travel type'
                : category === 'stay'
                  ? 'Stay type'
                  : category === 'reservation'
                    ? 'Reservation type'
                    : 'Activity type'}
            </FieldLabel>
            <Select
              value={subType}
              onChange={(e) => {
                setSubType(e.target.value as SubType);
                setFormValues({});
              }}
            >
              <option value="">
                {category === 'travel'
                  ? 'Select travel type'
                  : category === 'stay'
                    ? 'Select stay type'
                    : category === 'reservation'
                      ? 'Select reservation type'
                      : 'Select activity type'}
              </option>
              {SUBTYPE_OPTIONS[category].map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FieldGroup>
        )}

        {fields.length > 0 && <Divider />}
        {renderSection('booking')}
        {renderSection(undefined)}
        {renderSection('when')}
        {renderSection('details', 'Details')}
        {renderSection('notes')}
      </ScrollArea>
    </Form>
  );
};

export default EventForm;
